import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { keyBy } from 'lodash';

import { ContractorsApi } from '@/api/domains/contractors.api';
import { ContractorsQueryKeys } from '@/api/domains/contractors.query-keys';
import type { Contractor } from '@/api/domains/contractors.types';
import { useProfile } from '@/modules/profile';
import { prepareAddress } from '@/shared/helpers/format.helpers';

// Центральный склад = юрлицо: в корзине его pharmacy_id равен contractor_id (не 0).
export const useCentralWarehousePharmacyId = () => {
  const { profile } = useProfile();

  return profile?.contractor_id ?? 0;
};

// Список филиалов ограничен (~100), пагинация не требуется (см. IT-9213).
const PHARMACIES_LIMIT = 100;

export type Filial = {
  name: string;
  city: string;
};

export type GetFilial = (pharmacyId: number) => Filial;

type FilialsById = Record<string, Contractor>;

const FilialsByIdContext = createContext<FilialsById | null>(null);

// Общий провайдер: список филиалов (fetchContractors + keyBy) считается один раз на всё
// поддерево заказа, а не в каждом потребителе.
export const FilialNameProvider = ({ children }: PropsWithChildren) => {
  const { data: contractorsTree } = useQuery({
    queryKey: ContractorsQueryKeys.getContractorsTreeKey({ limit: PHARMACIES_LIMIT }),
    queryFn: () => ContractorsApi.fetchContractors({ limit: PHARMACIES_LIMIT }),
    staleTime: Infinity,
  });

  const filialsById = useMemo(
    () =>
      keyBy(
        (contractorsTree?.items ?? []).flatMap(parent => parent.children ?? []),
        'id',
      ),
    [contractorsTree],
  );

  return <FilialsByIdContext.Provider value={filialsById}>{children}</FilialsByIdContext.Provider>;
};

const useFilialsById = () => {
  const filialsById = useContext(FilialsByIdContext);

  if (!filialsById) {
    throw new Error('useFilialName must be used within a FilialNameProvider.');
  }

  return filialsById;
};

// Резолв pharmacy_id → название филиала. Метку центрального склада передаём аргументом,
// чтобы хук не зависел от i18n-неймспейса.
export const useFilialName = (centralWarehouseLabel: string) => {
  const centralWarehousePharmacyId = useCentralWarehousePharmacyId();
  const filialsById = useFilialsById();

  const getFilial = useCallback(
    (pharmacyId: number): Filial => {
      if (pharmacyId === centralWarehousePharmacyId) {
        return { name: centralWarehouseLabel, city: '' };
      }

      const filial = filialsById[pharmacyId];

      if (!filial) {
        return { name: '', city: '' };
      }

      return {
        name: filial.name || filial.full_name,
        city: filial.address ? prepareAddress(filial.address).cityAddress : '',
      };
    },
    [filialsById, centralWarehouseLabel, centralWarehousePharmacyId],
  );

  return { getFilial };
};
